// Import react elements
import { useCallback, useContext, useState, useEffect, useMemo, useRef } from "react";

// Import strich api calls
import { 
    getOrders,
    getOrder,
 } from "api/APIStrichCalls";

// Import context function
import { UserContext } from "global/context/UserContext";
import { AccountLastUpdatedContext } from "global/context/AccountLastUpdatedContext";

// Import components
import { GridItem } from "global/components/Grid";
import CreateButton from "global/components/CreateButton";
import OrdersTable from "./OrdersTable";
import OrderDetailsPopUp from "./OrderDetailsPopUp";
import LoadingScreen from "global/components/loading_ui/LoadingScreen";
import { useTableFilters } from "global/components/CustomisableTable";

// Import hooks
import useCustomisableTableActions from "global/hooks/useCustomisableTableActions";
import { useToast } from "global/hooks/useToast";

// Import utilities
import { FormatCurrency } from "global/utilities/ConvertCurrency";
import { formatDateFromMidnightString, formatDateToMidnightString } from "global/utilities/ConvertDate";

// Import css stylesheet
import "../OrdersPage.css";






// Format the columns for the orders table - Need to set these values in fetchOrders mapped array
const initialTableColumns = [

    // Select tick box at left of table rows - if we want to multi select orders and action them
    { key: 'select', label: '', resizable: false, minWidth: 40, maxWidth: 40, draggable: false, textFilter: false },

    { key: 'id', label: 'ID', resizable: false, minWidth: 70, maxWidth: 70, draggable: true, textFilter: true },

    { key: 'creationdate', label: 'Date', resizable: false, minWidth: 130, maxWidth: 130, draggable: true, textFilter: true },

    { key: 'connectionname', label: 'Store', resizable: true, minWidth: 120, maxWidth: 230, draggable: true, textFilter: true },

    { key: 'deliverycustname', label: 'Customer', resizable: true, minWidth: 120, maxWidth: 230, draggable: true, textFilter: true },

    { key: 'orderItemFirst', label: 'Order Items', resizable: true, minWidth: 200, maxWidth: 700, draggable: true, textFilter: true },

    { key: 'deliverycountry', label: 'Destination', resizable: false, minWidth: 155, maxWidth: 130, draggable: true, textFilter: true },
    
    { key: 'totalValueIncTax', label: 'Value', resizable: false, minWidth: 76, maxWidth: 76, draggable: true, textFilter: true, showTax: true },
    { key: 'totalValueExTax', label: 'Value', resizable: false, minWidth: 76, maxWidth: 76, draggable: true, textFilter: true, showTax: false },

    { key: 'netEarnings', label: 'Earnings', resizable: false, minWidth: 84, maxWidth: 84, draggable: true, textFilter: true },

    { key: 'production_status', label: 'Production', resizable: false, minWidth: 92, maxWidth: 92, draggable: true, textFilter: true },

    { key: 'status', label: 'Status', resizable: false, minWidth: 90, maxWidth: 90, draggable: true, textFilter: true },

];
// Note: Need to add something for parent child (linked) orders +



// Function to fetch specific order details
export const fetchOrder = async (orderId, setLoadingOrderDetails, setOrderDetails, setPopupOpen, toastError, token, searchOrderIdOnly = false) => {

    
    // Set loading state
    setLoadingOrderDetails(true);

    // Make api call
    const response = await getOrder(token, orderId);

    // If successful
    if (response?.success) {

        // Check if the results has anything in it
        if (response.results && Object.keys(response.results).length > 0) {

            // Set the orderDetails to api response
            setOrderDetails(response.results);

            // We have an order so open the pop up
            setPopupOpen(true);


        } else {

            // Clear loading
            setLoadingOrderDetails(false);

            // Set the error message
            const errorMessage = searchOrderIdOnly ? "Could not fetch order details: Order id not found." : "Could not find matching order."
            
            // Toast fail
            toastError({
                title: "Error",
                message: errorMessage,
                autoCloseMs: 4000
            });

        }

    } else {

        // Clear loading
        setLoadingOrderDetails(false);
       
        // Toast fail
        toastError({
            title: "Error",
            message: "Could not open order " + orderId + ".",
            apiErrorMessage: response.error_message,
            autoCloseMs: 4000
        });

    }

    // Set loading to false
    setLoadingOrderDetails(false);

};



const OrdersList = ({
        token, 
        authReady 
    }) => {

    // Extract the connections data and hsipping methods from the user context - allows us to use their data anywhere inside this component without needing to manually pass it down as a prop
    // Add shippingMethods below when ready 
    const { connectionsData } = useContext(UserContext);

    // Get the global account update markers
    const { lastUpdated } = useContext(AccountLastUpdatedContext);

    // Get the orders marker
    const ordersLastUpdated = lastUpdated?.orders_last_updated || null;

    // Soldiers. Soldiers, toast? Geddit?
    const { toastError } = useToast();

    // Set empty array state for the orders data retrieved from the API
    const [ordersData, setOrdersData] = useState([]);

    // Set loading state - while api call is fetching orders
    const [loadingOrders, setLoadingOrders] = useState(true);

    // Set empty array state for selected order
    const [orderDetails, setOrderDetails] = useState([]);

    // Set false state for adding new order
    const [addingNewOrder, setAddingNewOrder] = useState(false);

    // Set false state for the orders details pop up
    const [popupOpen, setPopupOpen] = useState(false);

    // Remember the document position before opening an order
    const ordersScrollPositionRef = useRef(0);

    // Keep restoring the position while the order popup can refresh the list underneath
    const restoreOrdersScrollPositionRef = useRef(false);

    // Set a false loading state for fetching the order details
    const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);

    // This controls whether the loading UI should actually be visible
    const [showOrderDetailsLoader, setShowOrderDetailsLoader] = useState(false);

    // Set error state for errors when fetching order
    const [error, setError] = useState(null);
  
    // Set state for storing the available service codes for this order's delivery country
    const [showTax, setShowTax] = useState(true);

    // Store the latest global orders marker without forcing fetchOrders to depend on it
    const ordersLastUpdatedRef = useRef(null);

    // Store the marker that was current when the current orders view was last loaded
    const loadedOrdersLastUpdatedRef = useRef(null);

    // Store the current all-orders date range for marker-triggered refreshes
    const allOrdersFromDateRef = useRef(null);
    const allOrdersToDateRef = useRef(null);

    // Prevent overlapping automatic refreshes
    const autoRefreshInProgressRef = useRef(false);


    // Keep the orders marker ref current
    useEffect(() => {

        // Store the latest marker
        ordersLastUpdatedRef.current = ordersLastUpdated;

    }, [ordersLastUpdated]);


    // Get the orders data and prepare it for the table
    // Use useCallback to create a stable fetchOrders function that depends on token and authReady
    const fetchOrders = useCallback(async (currentOrderId, fromDate, toDate, quietRefresh = false) => {

        // Clear the error message unless this is a quiet background refresh
        if (!quietRefresh) { setError(null); }

        // Check if there's no token or the authReady flag is false
        if (!authReady || !token)

            // Exit early
            return;

        // Set loading to true unless this is a quiet background refresh
        if (!quietRefresh) { setLoadingOrders(true); }

        try {

            // Use the current date range when a caller does not explicitly provide one
            let effectiveFromDate = fromDate || allOrdersFromDateRef.current;
            let effectiveToDate = toDate || allOrdersToDateRef.current;

            // On first load, use the same one-month range previously used by All Orders
            if (!effectiveFromDate || !effectiveToDate) {

                // Set today's date
                const todayDate = new Date();

                // Set the default start date to one calendar month ago
                const previousMonthDate = new Date();
                previousMonthDate.setMonth(previousMonthDate.getMonth() - 1);

                // Format both dates for the normal orders API
                effectiveFromDate = formatDateFromMidnightString(previousMonthDate.toISOString().split("T")[0]);
                effectiveToDate = formatDateToMidnightString(todayDate.toISOString().split("T")[0]);

            }

            // Load all orders within the selected range
            const response = await getOrders(token, effectiveFromDate, effectiveToDate);

            // Check if it successful and that the results is an array
            if (response.success && Array.isArray(response.results)) {

                // It is so map the orders returned
                const mappedOrders = response.results.map((order) => {

                    // Check if there is an array of order items and create a orderItems array with them or make it empty
                    const orderItems = Array.isArray(order.orderitems) ? order.orderitems : [];

                    // Find the matching connection to do a look up for the connection name
                    const matchingConnection = connectionsData?.find(connection => connection.id === order.connid);

                    // Check if there is an array of fulfilments and create a fulfilments array with them or make it empty
                    const fulfilments = Array.isArray(order?.fulfilments) ? order.fulfilments : [];

                    // Get the total value of the order with or without tax depending on user choice 
                    const totalValue = showTax ? order?.order_values?.total_inc_tax : order?.order_values?.total_exc_tax;

                    const totalValueExTax =  order?.order_values?.total_exc_tax && order?.currency ? FormatCurrency(order?.currency, order?.order_values?.total_exc_tax) : "";
                    const totalValueIncTax = order?.order_values?.total_inc_tax && order?.currency ? FormatCurrency(order?.currency, order?.order_values?.total_inc_tax) : "";
                    const netEarnings = order?.transaction_balance?.currency ? FormatCurrency(order.transaction_balance.currency, order?.transaction_balance?.amount ?? 0) : "";

                    // Get the total value of the order with or without tax depending on user choice 
                    //const _totalTax = order?.order_values?.total_tax ? `${Number(order?.order_values?.total_tax) * 10}%` : "";
                    const totalTax = order?.order_values?.total_tax;
                   
                    // Create an array with formatted data that we want to display in the orders table
                    return {

                        ...order,

                        orderitems: orderItems,
                        orderItemFirst: orderItems[0]?.title || '',
                        orderItemRemaining: orderItems.slice(1).map((item) => item.title || ''),
                        expandRow: orderItems.length > 1 ? true : false,

                        status: order.status || 'Unknown',
                        production_status: order.production_status || "N/A",
                        connectionname: matchingConnection?.connectionname || "Unknown Connection",

                        shippingcostinctax: (Number(order.shippingcostinctax) || 0).toFixed(2),

                        fulfilments: fulfilments,
                        isFulfilmentOrder: fulfilments?.length > 0 ? "Y" : "N",

                        totalValue: totalValue,
                        totalValueExTax: totalValueExTax,
                        totalValueIncTax: totalValueIncTax,
                        netEarnings: netEarnings,
                        totalTax: totalTax,
                    
                    };

                });

                // Update ordersData with the mapped orders data above
                setOrdersData(mappedOrders);

                // Remember the active date range for background refreshes and later saves
                allOrdersFromDateRef.current = effectiveFromDate;
                allOrdersToDateRef.current = effectiveToDate;

                // Remember the marker this orders view was loaded against
                loadedOrdersLastUpdatedRef.current = ordersLastUpdatedRef.current;

            } else {

                // Set the error message unless this is a quiet background refresh
                if (!quietRefresh) { setError("Error fetching order: ", response.error_message); }

                // Set loading state to false once the API call is complete unless this is a quiet background refresh
                if (!quietRefresh) { setLoadingOrders(false); }

            }

        } catch (err) {

            // Set the error message unless this is a quiet background refresh
            if (!quietRefresh) { setError("Error fetching order: ", err); }

        } finally {

            // Set loading state to false once the API call is complete unless this is a quiet background refresh
            if (!quietRefresh) { setLoadingOrders(false); }

            // Check if there's a current id - if so it's an order edit
            if (currentOrderId) {

                // Fetch the order that was edited so the new order data appears in the pop up form
                fetchOrder(currentOrderId, setLoadingOrderDetails, setOrderDetails, setPopupOpen, toastError, token );

            } 

        }
 
    // Dependencies that trigger a new instance of this function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authReady, connectionsData, token]);



    // useEffect to automatically fetch orders when the component mounts or when dependencies update
    useEffect(() => {

        // Check that there is connectionsData - we need it when mapping the orders data for the table
        if (connectionsData?.length > 0) {

        // Call fetchOrders on component mount or when token/authReady change
            fetchOrders();

        }

        // Dependencies that trigger a new instance of this function
    }, [fetchOrders, connectionsData]);


    // Refresh the current orders view when the global orders marker changes
    useEffect(() => {

        // Only refresh when auth and supporting data are ready
        if (!authReady || !token || !connectionsData?.length) { return; }

        // Ignore empty/default markers
        if (!ordersLastUpdated || ordersLastUpdated === "0001-01-01 00:00:00") { return; }

        // If this is the first marker seen, store it without refreshing
        if (!loadedOrdersLastUpdatedRef.current) {
            loadedOrdersLastUpdatedRef.current = ordersLastUpdated;
            return;
        }

        // Stop if the marker has not changed since the current orders view was loaded
        if (ordersLastUpdated === loadedOrdersLastUpdatedRef.current) { return; }

        // Stop if an automatic refresh is already running
        if (autoRefreshInProgressRef.current) { return; }

        // Mark the automatic refresh as running
        autoRefreshInProgressRef.current = true;

        // Reload the current orders date range quietly in the background
        fetchOrders(null, allOrdersFromDateRef.current, allOrdersToDateRef.current, true).finally(() => {

            // Mark the automatic refresh as finished
            autoRefreshInProgressRef.current = false;

        });

    }, [authReady, token, connectionsData, ordersLastUpdated, fetchOrders]);





    // This defines which filters the main transactions table supports
    const orderFilterKeys = useMemo(() => ["status", "connectionname"], []);


    // This builds the default filters for the orders table
    const buildOrderDefaultFilters = useCallback((filterOptions) => {

        // Include every available value by default
        return Object.entries(filterOptions || {}).reduce((accumulator, [key, values]) => {

            // This makes sure values is always an array
            const safeValues = Array.isArray(values) ? values : [];

            // Include every value in this filter group
            accumulator[key] = [...safeValues];

            return accumulator;

        }, {});

    }, []);


    // This creates the order table filters
    const orderFilters = useTableFilters(
        ordersData,
        orderFilterKeys,
        buildOrderDefaultFilters
    );



    // This delays the loader so the loading screen does not flash on very fast requests when getting the individual order details
    useEffect(() => {

        // If we are not loading, hide the loader immediately
        if (!loadingOrderDetails) {

            setShowOrderDetailsLoader(false);

            return;

        }

        // Wait a little before showing the loader
        const timer = setTimeout(() => {

            setShowOrderDetailsLoader(true);

        }, 300);

        // Clear the timer if loading finishes quickly
        return () => {

            clearTimeout(timer);

        };

    }, [loadingOrderDetails]);



    // Remember where the user currently is in the orders list
    const rememberOrdersScrollPosition = () => {

        // Start with the browser window position
        let currentScrollPosition = window.scrollY;

        // Prefer the document scrolling element when available
        if (document.scrollingElement) {
            currentScrollPosition = document.scrollingElement.scrollTop;
        }

        // Store the current vertical position
        ordersScrollPositionRef.current = currentScrollPosition;

        // Keep restoring it while the order popup can refresh the underlying list
        restoreOrdersScrollPositionRef.current = true;

    };


    // Restore the orders list after an order-popup refresh
    useEffect(() => {

        // Do nothing unless a previous position is waiting to be restored
        if (!restoreOrdersScrollPositionRef.current) {
            return;
        }

        // Wait until the orders have finished loading
        if (loadingOrders) {
            return;
        }

        // Wait until order data is available
        if (!Array.isArray(ordersData)) {
            return;
        }

        // Store both animation frame ids for cleanup
        let firstAnimationFrameId = 0;
        let secondAnimationFrameId = 0;

        // Wait one frame for the table to render
        firstAnimationFrameId = window.requestAnimationFrame(() => {

            // Wait another frame for its layout to settle
            secondAnimationFrameId = window.requestAnimationFrame(() => {

                // Normalise the stored position
                let restoredScrollPosition = Number(ordersScrollPositionRef.current);

                // Fall back to the top for an invalid stored position
                if (!Number.isFinite(restoredScrollPosition) || restoredScrollPosition < 0) {
                    restoredScrollPosition = 0;
                }

                // Restore the document position
                if (document.scrollingElement) {

                    document.scrollingElement.scrollTop = restoredScrollPosition;

                } else {

                    window.scrollTo(0, restoredScrollPosition);

                }

                // Keep it armed while the order popup remains open because another save can refresh again
                if (!popupOpen) {
                    restoreOrdersScrollPositionRef.current = false;
                }

            });

        });

        // Cancel pending restoration if another render happens first
        return () => {

            window.cancelAnimationFrame(firstAnimationFrameId);
            window.cancelAnimationFrame(secondAnimationFrameId);

        };

    }, [
        loadingOrders,
        ordersData,
        popupOpen,
    ]);


    // Function to handle selecting specific order
    const handleSelectOrder = (orderId) => {

        // Remember exactly where the user was before opening the order
        rememberOrdersScrollPosition();

        //console.log(orderId)

        // Clear the order details
        setOrderDetails(null);

        // Fetch the order we want
        fetchOrder(orderId, setLoadingOrderDetails, setOrderDetails, setPopupOpen, toastError, token );

    };


    // Function to handle quick search for any specific order can be based on id, venue id etc.
    const handleOrderQuickSearch = (input) => {

        // Remember the current page position before opening the order
        rememberOrdersScrollPosition();

        // Clear the order details
        setOrderDetails(null);

        // Fetch the order we want
        fetchOrder(input, setLoadingOrderDetails, setOrderDetails, setPopupOpen, toastError, token );

    };



    // State to hold the typed quick search input when searching a specific order id
    const [quickSearchInput, setQuickSearchInput] = useState('');



    // Function to render specific order search bar (input for order id and button to submit)
    const RenderSpecificOrderSearch = () => {

        return (

            <div className="specific-order-search">

                <form onSubmit={(e) => {
                    // Prevent form default behavior (page reload)
                    e.preventDefault();

                    // Trigger search logic
                    handleOrderQuickSearch(quickSearchInput);
                }}>

                    <div className="order-quick-search">

                        <input
                            type="text"
                            placeholder="Quick search..."
                            value={quickSearchInput}
                            onChange={(e) => setQuickSearchInput(e.target.value)}

                            className="order-search__input"
                        />

                        <CreateButton
                            title="Go"
                            size="s"
                            type="submit"
                            disabled={!quickSearchInput.trim() || popupOpen}
                            className="filters__filter-button"
                        />

                    </div>

                </form>

            </div>

        )

    };



    // Custom hook for actioning order/s
    const {
        tableActionsConfig
    } = useCustomisableTableActions({ tableType: "Orders", tableData: ordersData, fetchTableData: fetchOrders, handleSelectRow: handleSelectOrder });




    return (

        <GridItem>

            <div className="horizontal-align-content-center flex-justify-left-right">

                <h3>Orders</h3>

            </div>

            <br />

            <OrdersTable
                initialTableColumns={initialTableColumns}
                ordersData={ordersData}
                uniqueFilterOptions={orderFilters.filterOptions}
                defaultFilters={orderFilters.defaultFilters}
                handleSelectOrder={handleSelectOrder}
                fetchOrders={fetchOrders}
                quickSearchControl={RenderSpecificOrderSearch()}
                loadingOrders={loadingOrders}
                loadingOrdersErrorMessage={error}
                orderActions={tableActionsConfig}
                showTax={showTax}
                setShowTax={setShowTax}
            />

            <OrderDetailsPopUp
                isOpen={popupOpen}
                onClose={() => {
                    setPopupOpen(false);
                    setAddingNewOrder(false);
                }}
                loadingOrderDetails={loadingOrderDetails}
                error={error}
                orderDetails={orderDetails}
                addingNewOrder={addingNewOrder}
                setAddingNewOrder={setAddingNewOrder}
                fetchOrders={fetchOrders}
                connectionsData={connectionsData}
                ordersData={ordersData}
            />


            {/* Display loading screen if we need to */}
            {showOrderDetailsLoader && 

                <LoadingScreen />

            }

        </GridItem>

    );

};

export default OrdersList;